import { formatInTimeZone } from "date-fns-tz";

import {
  AGGREGATORS,
  AggregatorT,
  IncentiveCadenceT,
  IncentiveTierI,
  PAYOUT_TYPES,
  PayoutTypeT,
  RewardI,
} from "@/api/routes/incentives";

/**
 * Words and numbers for Rewards and Incentives (C-16 S1, GLE-8408). ET is the
 * business clock; money arrives in cents, rule amounts in dollars.
 */

export const ET_TIMEZONE = "America/New_York";

/** "$1,250" (whole dollars) or "$1,250.50" when there are cents. */
export const formatCents = (cents: number): string => {
  const dollars = cents / 100;
  const whole = Math.round(dollars) === dollars;

  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

/** Rule amounts (dollars, as entered). */
export const formatDollars = (dollars: number): string =>
  formatCents(Math.round(dollars * 100));

/** "Oct 31" in ET. */
export const formatEtDay = (iso: string): string =>
  formatInTimeZone(new Date(iso), ET_TIMEZONE, "MMM d");

/** "Oct 31, 2:00 PM ET". */
export const formatEtMoment = (iso: string): string =>
  `${formatInTimeZone(new Date(iso), ET_TIMEZONE, "MMM d, h:mm a")} ET`;

/**
 * A period's label: "Oct 1 – Oct 31". The end is exclusive, so the last day
 * shown is the instant before it.
 */
const lastInstant = (endIso: string): string =>
  new Date(new Date(endIso).getTime() - 1).toISOString();

/** The last ET day before an exclusive end: "Oct 31" for Nov 1 00:00 ET. */
export const formatEtLastDay = (endIso: string): string =>
  formatEtDay(lastInstant(endIso));

export const formatPeriod = (start: string, end: string): string => {
  const from = formatEtDay(start);
  const to = formatEtDay(lastInstant(end));

  return from === to ? from : `${from} – ${to}`;
};

/** "3d 4h left", "5h 20m left", "Ended". */
export const formatTimeLeft = (endIso: string, now: Date): string => {
  const ms = new Date(endIso).getTime() - now.getTime();

  if (ms <= 0) {
    return "Ended";
  }

  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);

  if (days >= 1) {
    return `${days}d ${hours}h left`;
  }

  const minutes = Math.floor((ms % 3_600_000) / 60_000);

  return `${hours}h ${minutes}m left`;
};

/** When a reward runs, in a few words, as of `now`. */
export const describeWhen = (
  reward: Pick<RewardI, "segment" | "start_at" | "end_at" | "cadence">,
  now: Date
): string => {
  if (reward.segment === "upcoming" && reward.start_at) {
    return `Starts ${formatEtDay(reward.start_at)}`;
  }

  if (reward.segment === "past") {
    // The end is exclusive: show the last day it ran.
    return reward.end_at ? `Ended ${formatEtLastDay(reward.end_at)}` : "Ended";
  }

  if (reward.end_at) {
    return formatTimeLeft(reward.end_at, now);
  }

  // No end: a "once" incentive runs month by month (ET months).
  return reward.cadence === "once"
    ? "Every month"
    : CADENCE_LABELS[reward.cadence];
};

export const CADENCE_LABELS: Record<IncentiveCadenceT, string> = {
  once: "Runs once",
  weekly: "Every week",
  daily: "Every day",
  live_race: "First to the goal",
};

/** What a metric is called on a card. */
export const METRIC_LABELS: Record<AggregatorT, string> = {
  [AGGREGATORS.COUNT_MEETINGS]: "Qualified meetings",
  [AGGREGATORS.BOOKED_COUNT]: "Booked meetings",
  [AGGREGATORS.SHOW_RATE]: "Show rate",
  [AGGREGATORS.SUM_TOTAL_PAYOUT]: "GMV",
  [AGGREGATORS.SUM_USER_PAYOUT]: "Your earnings",
};

const sortedPlaces = (tiers: Array<IncentiveTierI>) =>
  tiers
    .filter((tier) => (tier.place ?? 0) > 0)
    .sort((a, b) => (a.place ?? 0) - (b.place ?? 0));

const sortedThresholds = (tiers: Array<IncentiveTierI>) =>
  tiers
    .filter((tier) => (tier.threshold ?? 0) > 0)
    .sort((a, b) => (a.threshold ?? 0) - (b.threshold ?? 0));

/** The prize in a few words: "$50 per qualified meeting", "$300 / $200 / $100". */
export const describePrize = (
  reward: Pick<
    RewardI,
    "payout_type" | "payout_value" | "tiers" | "trigger" | "rank" | "cadence"
  >
): string => {
  const value = reward.payout_value;

  switch (reward.payout_type as PayoutTypeT) {
    case PAYOUT_TYPES.FLAT_PER_MEETING:
      return `${formatDollars(value)} per meeting`;
    case PAYOUT_TYPES.FLAT_PER_REP:
    case PAYOUT_TYPES.FLAT_PER_QUALIFIER:
      return `${formatDollars(value)} when you hit the goal`;
    case PAYOUT_TYPES.PCT_GMV:
    case PAYOUT_TYPES.PCT_TOTAL_PAYOUT:
      return `${value}% of GMV`;
    case PAYOUT_TYPES.PCT_USER_PAYOUT:
      return `${value}% of your earnings`;
    case PAYOUT_TYPES.THRESHOLD_TIERED: {
      const tiers = sortedThresholds(reward.tiers);
      const top = tiers[tiers.length - 1];

      return top ? `Up to ${formatDollars(top.amount)} bonus` : "Bonus ladder";
    }
    case PAYOUT_TYPES.RANKED_TIERED: {
      const places = sortedPlaces(reward.tiers);

      return places.length
        ? places.map((place) => formatDollars(place.amount)).join(" / ")
        : "Prizes by place";
    }
    case PAYOUT_TYPES.FLAT_WINNER: {
      if (reward.cadence === "live_race") {
        return `${formatDollars(value)} to the first`;
      }

      const topN = reward.rank?.top_n ?? 1;

      return topN > 1
        ? `${formatDollars(value)} each to the top ${topN}`
        : `${formatDollars(value)} to the winner`;
    }
    case PAYOUT_TYPES.GROWTH_TIERED: {
      const top = [...(reward.trigger?.growth_tiers ?? [])].sort(
        (a, b) => b.amount - a.amount
      )[0];

      return top
        ? `Up to ${formatDollars(top.amount)}`
        : "Beat your best month";
    }
    case PAYOUT_TYPES.TEAM_TIERED: {
      const top = [...(reward.trigger?.team_tiers ?? [])].sort(
        (a, b) => b.amount - a.amount
      )[0];

      return top ? `Up to ${formatDollars(top.amount)} each` : "Team goal";
    }
    default:
      return "";
  }
};

/** How it works, as short lines a caller reads once. */
export const describeRules = (
  reward: Pick<
    RewardI,
    | "payout_type"
    | "payout_value"
    | "tiers"
    | "trigger"
    | "rank"
    | "race"
    | "rule"
    | "cadence"
    | "start_at"
    | "end_at"
  >
): Array<string> => {
  const lines: Array<string> = [];
  const trigger = reward.trigger ?? {};
  const metric =
    METRIC_LABELS[
      (reward.rank?.aggregator ??
        reward.rule?.aggregator ??
        "count_meetings") as AggregatorT
    ] ?? "Qualified meetings";

  switch (reward.payout_type as PayoutTypeT) {
    case PAYOUT_TYPES.FLAT_PER_MEETING:
      lines.push(
        trigger.qualifying_count_min && trigger.qualifying_count_min > 1
          ? `${formatDollars(reward.payout_value)} for every qualified meeting from your ${ordinal(trigger.qualifying_count_min)} on.`
          : `${formatDollars(reward.payout_value)} for every qualified meeting.`
      );
      break;
    case PAYOUT_TYPES.THRESHOLD_TIERED: {
      const what = trigger.paid_only
        ? "your paid earnings from qualified meetings"
        : "your earnings from qualified meetings";

      lines.push(`The bonus is the highest step ${what} reach:`);
      sortedThresholds(reward.tiers).forEach((tier) =>
        lines.push(
          `${formatDollars(tier.threshold ?? 0)} → ${formatDollars(tier.amount)}`
        )
      );
      (trigger.milestones ?? []).forEach((milestone) =>
        lines.push(
          `${formatDollars(milestone.threshold)} unlocks: ${milestone.label}.`
        )
      );
      break;
    }
    case PAYOUT_TYPES.RANKED_TIERED:
      lines.push(`Ranked by ${metric.toLowerCase()}.`);
      sortedPlaces(reward.tiers).forEach((place) =>
        lines.push(
          `${ordinal(place.place ?? 0)} place: ${formatDollars(place.amount)}`
        )
      );
      break;
    case PAYOUT_TYPES.FLAT_WINNER:
      lines.push(
        reward.cadence === "live_race"
          ? `The first to ${reward.race?.target_count ?? 1} qualified meetings wins ${formatDollars(reward.payout_value)}.`
          : `The top ${reward.rank?.top_n ?? 1} by ${metric.toLowerCase()} win ${formatDollars(reward.payout_value)} each.`
      );
      break;
    case PAYOUT_TYPES.GROWTH_TIERED:
      lines.push("Beat your best month's earnings:");
      [...(trigger.growth_tiers ?? [])]
        .sort((a, b) => a.multiplier - b.multiplier)
        .forEach((tier) =>
          lines.push(
            `${tier.multiplier}× your best month → ${formatDollars(tier.amount)}`
          )
        );
      if (trigger.prior_period_min_value) {
        lines.push(
          `Your best month needs to be at least ${formatDollars(trigger.prior_period_min_value)}.`
        );
      }
      break;
    case PAYOUT_TYPES.TEAM_TIERED:
      lines.push(
        trigger.team_metric === "user_payout"
          ? "Your team's combined earnings set everyone's bonus:"
          : "Your team's combined qualified meetings set everyone's bonus:"
      );
      [...(trigger.team_tiers ?? [])]
        .sort((a, b) => a.threshold - b.threshold)
        .forEach((tier) =>
          lines.push(
            `${trigger.team_metric === "user_payout" ? formatDollars(tier.threshold) : tier.threshold} → ${formatDollars(tier.amount)} each`
          )
        );
      break;
    default:
      lines.push(describePrize(reward) + ".");
  }

  const statuses = describeStatusFilters(reward.rule);

  if (statuses) {
    lines.push(statuses);
  }

  if (trigger.booked_start && trigger.booked_end) {
    lines.push(
      `Counts meetings booked ${trigger.booked_start} to ${trigger.booked_end} (ET).`
    );
  }

  if (trigger.date_start || trigger.date_end) {
    lines.push(
      trigger.date_start && trigger.date_end
        ? `Counts meetings held ${trigger.date_start} to ${trigger.date_end} (ET).`
        : trigger.date_start
          ? `Counts meetings held from ${trigger.date_start} (ET).`
          : `Counts meetings held until ${trigger.date_end} (ET).`
    );
  }

  if (
    trigger.qualifying_count_min &&
    reward.payout_type !== PAYOUT_TYPES.FLAT_PER_MEETING
  ) {
    lines.push(
      `Needs at least ${trigger.qualifying_count_min} qualified meetings.`
    );
  }

  if (trigger.booked_min) {
    lines.push(`Needs at least ${trigger.booked_min} booked meetings.`);
  }

  if (trigger.show_rate_min) {
    lines.push(
      `Needs a show rate of ${Math.round(trigger.show_rate_min * 100)}% or more.`
    );
  }

  if (trigger.qualifying_value_min) {
    lines.push(
      `Needs at least ${formatDollars(trigger.qualifying_value_min)} of qualified GMV.`
    );
  }

  const workspaceGoal = trigger.workspace_total_min;

  if (workspaceGoal && workspaceGoal.value > 0) {
    lines.push(
      `Pays only once the whole workspace reaches ${describeWorkspaceGoal(workspaceGoal)}.`
    );
  }

  if (reward.cadence === "weekly") {
    lines.push("Each week (Monday to Sunday, ET) pays on its own.");
  }

  return lines;
};

const WORKSPACE_GOAL_UNITS: Record<string, (value: number) => string> = {
  qualifying_count: (value) =>
    `${value.toLocaleString("en-US")} qualified meetings`,
  booked_count: (value) => `${value.toLocaleString("en-US")} booked meetings`,
  qualifying_value: (value) => `${formatDollars(value)} of qualified GMV`,
};

const describeWorkspaceGoal = ({
  metric,
  value,
}: {
  metric: string;
  value: number;
}) =>
  (WORKSPACE_GOAL_UNITS[metric] ?? WORKSPACE_GOAL_UNITS.qualifying_count)(
    value
  );

/** The status filters callers see (`rule.filters`), as one sentence. */
const describeStatusFilters = (
  rule: RewardI["rule"] | undefined
): string | null => {
  const filters = (rule?.filters ?? []).filter(
    (filter) => filter.field === "status"
  );
  const values = (filter: { value: unknown }) =>
    (Array.isArray(filter.value) ? filter.value : [filter.value])
      .map((value) => String(value).toLowerCase())
      .join(" or ");
  const only = filters.filter(
    (filter) => filter.op === "in" || filter.op === "eq"
  );
  const not = filters.filter(
    (filter) => filter.op === "nin" || filter.op === "neq"
  );
  const parts = [
    ...only.map((filter) => `that are ${values(filter)}`),
    ...not.map((filter) => `that aren't ${values(filter)}`),
  ];

  return parts.length > 0
    ? `Counts only meetings ${parts.join(" and ")}.`
    : null;
};

/**
 * A rep in a contest won by place who hasn't met its goals has no place
 * (the server lists them after the ranked reps). `trigger_met` decides, not
 * `rank`: the engine places only reps who meet the goals, and the server
 * fills an unplaced rep's `rank` with their row position. Races rank by
 * progress and flag only the winner, so they never count as unranked.
 */
export const isUnranked = (
  reward: Pick<RewardI, "payout_type" | "cadence">,
  standing: { trigger_met: boolean }
): boolean =>
  !standing.trigger_met &&
  reward.cadence !== "live_race" &&
  (reward.payout_type === PAYOUT_TYPES.RANKED_TIERED ||
    reward.payout_type === PAYOUT_TYPES.FLAT_WINNER);

export const UNRANKED_HINT = "Hasn't met the goals yet, so no place";

export const ordinal = (n: number): string => {
  const rem100 = n % 100;

  if (rem100 >= 11 && rem100 <= 13) {
    return `${n}th`;
  }

  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};
